import { useNavigate } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link2, History, ExternalLink, AlertCircle } from 'lucide-react';
import type { AuditException } from '@/types/asset-audit';
import { format } from 'date-fns';

interface RelatedInformationSectionProps {
  exception: AuditException;
  assetId: string;
}

// Mock asset history - replace with actual API call
const mockAssetHistory = [
  {
    id: '1',
    date: '2024-01-10T10:00:00Z',
    event: 'Location Changed',
    details: 'Moved from Building A to Building B',
    user: 'John Doe',
  },
  {
    id: '2',
    date: '2024-01-05T14:30:00Z',
    event: 'Audit Completed',
    details: 'Annual audit - No issues found',
    user: 'Jane Smith',
  },
  {
    id: '3',
    date: '2023-12-15T09:00:00Z',
    event: 'Condition Updated',
    details: 'Condition changed from Good to Fair',
    user: 'Mike Johnson',
  },
  {
    id: '4',
    date: '2023-12-01T11:00:00Z',
    event: 'Maintenance Completed',
    details: 'Routine maintenance performed',
    user: 'Sarah Wilson',
  },
  {
    id: '5',
    date: '2023-11-20T15:30:00Z',
    event: 'Asset Registered',
    details: 'Asset added to system',
    user: 'Admin User',
  },
  {
    id: '6',
    date: '2023-11-15T10:00:00Z',
    event: 'Initial Purchase',
    details: 'Asset purchased from supplier',
    user: 'Procurement Team',
  },
];

export function RelatedInformationSection({ assetId }: Readonly<RelatedInformationSectionProps>) {
  const navigate = useNavigate();
  
  // TODO: Related exceptions should be fetched from API in production
  // This is intentionally an empty array as a placeholder for future API integration
  // SonarQube: This empty array is expected - the UI handles the empty state gracefully
  const relatedExceptions: AuditException[] = [];

  // Show only latest 5 items
  const latestHistory = mockAssetHistory.slice(0, 5);
  const latestRelatedExceptions = relatedExceptions.slice(0, 5);

  const handleViewFullHistory = () => {
    // Navigate to asset detail page with history tab active
    navigate(`/assets/${assetId}?tab=history`);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Link2 className="h-5 w-5 text-muted-foreground" />
        <Label className="text-base font-semibold">Related Information</Label>
      </div>

      <div className="space-y-4">
        {/* Related Exceptions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              Related Exceptions
            </h4>
            {relatedExceptions.length > 0 && (
              <Badge variant="secondary">
                {relatedExceptions.length > 5 ? '5+' : relatedExceptions.length}
              </Badge>
            )}
          </div>
          
          {latestRelatedExceptions.length > 0 ? (
            <div className="space-y-2">
              {latestRelatedExceptions.map((relException) => (
                <button
                  key={relException.id}
                  className="w-full text-left p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {relException.exceptionType}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(relException.reportedDate), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <Badge variant="outline" className="ml-2 flex-shrink-0">
                      {relException.status}
                    </Badge>
                  </div>
                </button>
              ))}
              {relatedExceptions.length > 5 && (
                <p className="text-xs text-muted-foreground text-center pt-1">
                  Showing latest 5 of {relatedExceptions.length} exceptions
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-6 bg-muted/30 rounded-lg">
              <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No other exceptions for this asset</p>
            </div>
          )}
        </div>

        {/* Asset History */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              Asset History
            </h4>
            {mockAssetHistory.length > 5 && (
              <Badge variant="secondary">
                {mockAssetHistory.length > 5 ? '5+' : mockAssetHistory.length}
              </Badge>
            )}
          </div>
          
          <div className="space-y-2">
            {latestHistory.map((item) => (
              <div
                key={item.id}
                className="p-3 border rounded-lg bg-muted/30"
              >
                <div className="flex items-start justify-between mb-1">
                  <span className="text-sm font-medium">{item.event}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                    {format(new Date(item.date), 'MMM dd, yyyy')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{item.details}</p>
                <p className="text-xs text-muted-foreground mt-1">by {item.user}</p>
              </div>
            ))}
            {mockAssetHistory.length > 5 && (
              <p className="text-xs text-muted-foreground text-center pt-1">
                Showing latest 5 of {mockAssetHistory.length} history items
              </p>
            )}
          </div>
        </div>

        {/* View Full History Button */}
        <Button 
          variant="outline" 
          className="w-full" 
          size="sm"
          onClick={handleViewFullHistory}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          View Full Asset History
        </Button>
      </div>
    </div>
  );
}
